import format from "lib/format";
import { fetchCategories, fetchCatalogue } from "./endpoints";
import {
  CACHE_DURATION,
  SORT_ORDER_TO_TYPE,
  CATALOGUE_PAGE_SIZE,
} from "./constants";
import { filterService } from "./services/filterService";
import { sortService } from "./services/sortService";

/**
 * Fetches other categories data from the API
 * @param {boolean} isPublic - Whether to use public endpoint (default: false)
 * @returns {Promise<Array>} Promise resolving to array of categories
 */
export const getOtherCategories = async (isPublic = false) => {
  const categories = await fetchCategories();
  return categories;
};

let cachedCourses = null;
let cacheTimestamp = null;

/**
 * Clears the cache for catalogue data
 */
export const clearCatalogueCache = () => {
  cachedCourses = null;
  cacheTimestamp = null;
};

/**
 * Checks if cached data is still valid
 * @returns {boolean} True if cache is valid
 */
const isCacheValid = () => {
  if (!cachedCourses || !cacheTimestamp) {
    return false;
  }
  const now = Date.now();
  return now - cacheTimestamp < CACHE_DURATION;
};

/**
 * Shared function to fetch catalogue data with caching
 * @param {boolean} isPublic - Whether to use public endpoint (default: false)
 * @returns {Promise<Array>} Promise resolving to array of courses
 */
const fetchCatalogueDataShared = async (isPublic = false) => {
  if (isCacheValid()) {
    return cachedCourses;
  }

  // Fetch fresh data
  const response = await fetchCatalogue(isPublic);
  cachedCourses = (response || []).map((course) => ({
    ...course,
    imageUrl: course?.imageUrl
      ? format.url(`/${course.imageUrl}`)
      : course?.imageUrl,
  }));
  cacheTimestamp = Date.now();

  return cachedCourses;
};

/**
 * Applies search filter to courses
 * @param {Array} courses - Array of courses
 * @param {string} searchInput - Search text
 * @returns {Array} Filtered courses
 */
const applySearchFilter = (courses, searchInput) => {
  if (!searchInput) {
    return courses;
  }
  return filterService.filterBySearchText(courses, searchInput);
};

/**
 * Applies category filters to courses
 * @param {Array} courses - Array of courses
 * @param {Object} categories - Category filter object
 * @returns {Array} Filtered courses
 */
const applyCategoryFilters = (courses, categories) => {
  const hasActiveCategories = Object.values(categories).some(
    (cat) => cat !== 0,
  );
  if (!hasActiveCategories) {
    return courses;
  }
  return filterService.filterByCategories(courses, categories);
};

/**
 * Applies date filters to courses
 * @param {Array} courses - Array of courses
 * @param {string|null} startDate - Start date filter
 * @param {string|null} endDate - End date filter
 * @returns {Array} Filtered courses
 */
const applyDateFilters = (courses, startDate, endDate) => {
  let filtered = courses;
  if (startDate) {
    filtered = filterService.filterByStartDate(filtered, startDate);
  }
  if (endDate) {
    filtered = filterService.filterByEndDate(filtered, endDate);
  }
  return filtered;
};

/**
 * Applies session filters to courses
 * @param {Array} courses - Array of courses
 * @param {string} sessionRegion - Session region filter
 * @param {string} sessionCity - Session city filter
 * @returns {Array} Filtered courses
 */
const applySessionFilters = (courses, sessionRegion, sessionCity) => {
  let filtered = courses;
  if (sessionRegion) {
    filtered = filterService.filterBySessionRegion(filtered, sessionRegion);
  }
  if (sessionCity) {
    filtered = filterService.filterBySessionCity(filtered, sessionCity);
  }
  return filtered;
};

/**
 * Applies sorting to courses
 * @param {Array} courses - Array of courses
 * @param {number} sortOrderParam - Sort order parameter
 * @returns {Array} Sorted courses
 */
const applySorting = (courses, sortOrderParam) => {
  if (sortOrderParam === undefined || sortOrderParam === null) {
    return courses;
  }
  const sortOrderInt = parseInt(sortOrderParam, 10);
  const sortType = SORT_ORDER_TO_TYPE[sortOrderInt] || "title";
  return sortService.sort(courses, sortType);
};

/**
 * Helper function to apply filters and sorting to the full dataset using existing services
 * @param {Array} courses - Array of courses to filter and sort
 * @param {Object} filters - Filter parameters object
 * @param {string} filters.searchInput - Search text to filter courses
 * @param {number} filters.category1 - First category filter ID
 * @param {number} filters.category2 - Second category filter ID
 * @param {number} filters.category3 - Third category filter ID
 * @param {number} filters.category4 - Fourth category filter ID
 * @param {number} filters.category5 - Fifth category filter ID
 * @param {number} filters.sortOrderParam - Sort order parameter
 * @param {string|null} filters.startDate - Start date filter (inclusive)
 * @param {string|null} filters.endDate - End date filter (inclusive)
 * @param {string} filters.sessionRegion - Session region filter
 * @param {string} filters.sessionCity - Session city filter
 * @returns {Array} Filtered and sorted courses
 */
export const applyFiltersToFullDataset = (courses, filters) => {
  const categories = {
    category1: filters.category1,
    category2: filters.category2,
    category3: filters.category3,
    category4: filters.category4,
    category5: filters.category5,
  };

  let filteredCourses = applySearchFilter(courses, filters.searchInput);
  filteredCourses = applyCategoryFilters(filteredCourses, categories);
  filteredCourses = applyDateFilters(
    filteredCourses,
    filters.startDate,
    filters.endDate,
  );
  filteredCourses = applySessionFilters(
    filteredCourses,
    filters.sessionRegion,
    filters.sessionCity,
  );
  filteredCourses = applySorting(filteredCourses, filters.sortOrderParam);

  return filteredCourses;
};

/**
 * @typedef {Object} CatalogueFilters
 * @property {string} searchInput - Search text to filter courses
 * @property {number} category1 - First category filter ID
 * @property {number} category2 - Second category filter ID
 * @property {number} category3 - Third category filter ID
 * @property {number} category4 - Fourth category filter ID
 * @property {number} category5 - Fifth category filter ID
 * @property {number} pageNum - Page number for pagination
 * @property {number} sortOrderParam - Sort order parameter
 * @property {string|null} startDate - Start date filter (inclusive)
 * @property {string|null} endDate - End date filter (inclusive)
 * @property {string} sessionRegion - Session region filter
 * @property {string} sessionCity - Session city filter
 */

/**
 * Internal helper to get filtered courses
 * @param {CatalogueFilters} filters - Filter parameters object
 * @param {boolean} isPublic - Whether to use public endpoint (default: false)
 * @returns {Promise<Array>} Promise resolving to filtered courses
 */
const getFilteredCoursesInternal = async (filters, isPublic = false) => {
  const courses = await fetchCatalogueDataShared(isPublic);
  return applyFiltersToFullDataset(courses, filters);
};

/**
 * Fetches catalogue data with filtering, sorting, and pagination
 * @param {CatalogueFilters} filters - Filter parameters object
 * @param {boolean} isPublic - Whether to use public endpoint (default: false)
 * @returns {Promise<Array>} Promise resolving to paginated array of courses
 */
export const getAsyncCatalogueData = async (filters, isPublic = false) => {
  const allFilteredCourses = await getFilteredCoursesInternal(
    filters,
    isPublic,
  );

  // Apply pagination
  const pageNum = filters.pageNum || 1;
  const startIndex = (pageNum - 1) * CATALOGUE_PAGE_SIZE;
  const endIndex = startIndex + CATALOGUE_PAGE_SIZE;
  return allFilteredCourses.slice(startIndex, endIndex);
};

/**
 * Gets all filtered courses without pagination
 * @param {CatalogueFilters} filters - Filter parameters object
 * @param {boolean} isPublic - Whether to use public endpoint (default: false)
 * @returns {Promise<Array>} Promise resolving to all filtered courses
 */
export const getAllFilteredCourses = async (filters, isPublic = false) => {
  return getFilteredCoursesInternal(filters, isPublic);
};

/**
 * Gets total count of courses for pagination
 * @param {CatalogueFilters} filters - Filter parameters object
 * @param {boolean} isPublic - Whether to use public endpoint (default: false)
 * @returns {Promise<number>} Promise resolving to total count of filtered courses
 */
export const getTotalCount = async (filters, isPublic = false) => {
  const allFilteredCourses = await getFilteredCoursesInternal(
    filters,
    isPublic,
  );
  return allFilteredCourses.length;
};

/**
 * Filters learning activities and triggers data fetch
 * @param {Event} event - Form event
 * @param {string} searchInput - Search text to filter courses
 * @param {Object} updatedCategoriesObject - Object containing category filter values
 * @param {Function} fetchCatalogueData - Function to fetch catalogue data
 */
export const filterLearnings = (
  event,
  searchInput,
  updatedCategoriesObject,
  fetchCatalogueData,
) => {
  event.preventDefault();

  fetchCatalogueData(
    searchInput,
    updatedCategoriesObject.category1,
    updatedCategoriesObject.category2,
    updatedCategoriesObject.category3,
    updatedCategoriesObject.category4,
    updatedCategoriesObject.category5,
  );
};
